import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useModuleConfig } from '@/hooks/queries/admin/useModuleConfig';
import type { IemModuleConfig } from '@/types/moduleConfig';
import { useIeKitLabels } from '@/hooks/useIeKitLabels';
import { useRestrictedProgramLabels } from '@/hooks/useRestrictedProgramLabels';
import {
  Alert,
  Button,
  Stack,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import { West as WestIcon } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { WorkflowModalLayout } from '@/components/shared/workflows/WorkflowModalLayout';
import { SignatureCaptureDialog } from '@/components/shared/SignatureCaptureDialog';
import { DocumentFieldsModal } from '@/components/documents';
import { extractErrorMessage } from '../workflowUtils';
import { IssueCartSidebar } from './IssueCartSidebar';
import { IssueCustomerSummaryModal } from './IssueCustomerSummaryModal';
import { useIssueAdditionalItemsTab } from './IssueAdditionalItemsTab';
import { useIssueIeTab } from './IssueIeTab';
import { useIssueBuyProgramTab } from './IssueBuyProgramTab';
import { useIssueRestrictedProgramTab } from './IssueRestrictedProgramTab';
import { RestrictedProgramCompleteIssueDialog } from './RestrictedProgramCompleteIssueDialog';
import { Sf1034SignatureDialog } from './Sf1034SignatureDialog';
import { iemService } from '@/services/iem';
import { entityService } from '@/services/EntityService';
import { documentService } from '@/services/documents';
import type { PdfFormItem, DocumentPreviewResponse } from '@/services/documents';
import { useNotifications } from '@/stores/useNotificationStore';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/useAuthStore';
import { useEntities } from '@/hooks';
import type { Entity } from '@/types/Entity';
import { isUuid, formatNomenclatureWithSize } from '@equip/utils';
import { logger } from '@equip/utils/logger';
import { usePersonLabels } from '@/hooks/usePersonLabels';
import { openDocumentWithAuth, preOpenWindow } from '@/utils/pdfHelpers';
import { getActorIdFromToken } from '@/utils/jwtUtils';
import {
  generateChangeDocument,
  formatCurrency,
  type ChangeDocumentLineItem,
} from '@/utils/generateChangeDocument';
import { useMemberEmail } from '@/hooks/useMemberEmail';
import { useWorkflowCart, cartQueryKeys } from '@/hooks/queries/cart/useCartQueries';
import { useUpsertSizeAllocationCart } from '@/hooks/mutations/cart/useCartMutations';
import { cartService, ScanningStatus } from '@/services/cart';
import type { IEMCustomer } from '@/services/iem/types';
import type { IssueSizingSelection, QuantitySummaryEntry } from '@/services/iem/types';
import type { SizeAllocationPlaceholder } from './IssueCartSidebar';
import { useAllCatalogChildren } from '@/hooks/queries/iem/useIEMQueries';
import {
  buildIssueSizingSelectionCandidates,
  type IssueSizingSelectionCandidate,
} from './issueSizingSelections';
import { buildBuyProgramRequestFragment } from './buyProgramUtils';
import type {
  IssuePlannerItem,
  IssuePlannerCartItem,
  IssueAdditionalCartItem,
  IssueBuyProgramCartItem,
} from './types';

const log = logger.child({ component: 'IssueWorkflow' });

/** Build quantity summary for transaction logging from cart state. */
const buildQuantitySummary = (
  ieCartItems: IssuePlannerCartItem[],
  iePlannerItems: IssuePlannerItem[],
  additionalCartItems: IssueAdditionalCartItem[],
): QuantitySummaryEntry[] => {
  const entries: QuantitySummaryEntry[] = [];

  for (const cartItem of ieCartItems) {
    const plannerItem = iePlannerItems.find((p) => p.id === cartItem.plannerItemId);
    const requestedQty = plannerItem?.need ?? cartItem.assetIds.length;
    const targetQty = plannerItem?.quantityToIssue ?? requestedQty;
    const issuedQty = cartItem.assetIds.length;
    entries.push({
      catalogId: cartItem.selectedCatalogId ?? cartItem.id,
      catalogName: cartItem.name,
      requestedQty,
      targetQty,
      issuedQty,
      variance: requestedQty - issuedQty,
    });
  }

  for (const cartItem of additionalCartItems) {
    const issuedQty = cartItem.assetIds.length;
    entries.push({
      catalogId: cartItem.groupId,
      catalogName: cartItem.name,
      requestedQty: issuedQty,
      targetQty: issuedQty,
      issuedQty,
      variance: 0,
    });
  }

  return entries;
};

/** Close a pre-opened window if it's still open. */
const closeIfOpen = (win: Window | null | undefined) => {
  if (win && !win.closed) win.close();
};

export interface IssueWorkflowModalProps {
  open: boolean;
  onClose: () => void;
  /** True when opening from Saved Issue Carts. */
  fromSavedCart?: boolean;
  unitName?: string;
  memberName?: string;
  customerId?: string;
  memberEmail?: string;
  /** Full customer data for Change Document generation */
  customer?: IEMCustomer;
  onCompleted?: (result: {
    memberId: string;
    issuedCount: number;
    receiptIds: string[];
    transactionLogIds: string[];
  }) => void;
}

export const IssueWorkflowModal = ({
  open,
  onClose,
  fromSavedCart = false,
  unitName,
  memberName,
  customerId,
  memberEmail: memberEmailProp,
  customer,
  onCompleted,
}: IssueWorkflowModalProps) => {
  const { config: iemConfig } = useModuleConfig<IemModuleConfig>('iem');
  const iemLabels = iemConfig.features.front_window;
  const { label: ieKitLabel } = useIeKitLabels('front_window');
  const restrictedProgram = useRestrictedProgramLabels();
  const person = usePersonLabels('front_window');
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const { user, isComptroller, isHr } = useAuth();
  // HR carries the same restricted-program-specific assign-only permission as
  // comptroller/finance: they can only issue restricted (buy) program items
  // (soc_tag=true), not standard IE kit items.
  const isRestrictedProgramAssignOnly = isComptroller || isHr;
  useMemberEmail(customerId, memberEmailProp); // Hook for side effects (email notification)
  const { data: cartData, isRefetching: isCheckingForScanUpdates } = useWorkflowCart(customerId, {
    refetchInterval: (data) => {
      const status = data?.cart?.scanning_status;
      // Poll every 30 seconds while waiting for mobile scanning to complete
      if (status === ScanningStatus.ReadyToScan || status === ScanningStatus.InProgress) {
        return 30_000;
      }
      return false;
    },
  });
  const placeholders = useMemo(() => cartData?.placeholders ?? [], [cartData?.placeholders]);
  const upsertSizeAllocationMutation = useUpsertSizeAllocationCart();
  const scanningStatus = cartData?.cart?.scanning_status;
  const isCartLocked =
    scanningStatus === ScanningStatus.ReadyToScan ||
    scanningStatus === ScanningStatus.InProgress ||
    scanningStatus === ScanningStatus.Complete;
  const isWaitingForScan =
    scanningStatus === ScanningStatus.ReadyToScan || scanningStatus === ScanningStatus.InProgress;
  const [catalogLookup, setCatalogLookup] = useState<
    Record<string, { title: string; lin?: string; nsn?: string }>
  >({});

  const [activeTab, setActiveTab] = useState(0);
  const [isCompleting, setIsCompleting] = useState(false);
  const [completionError, setCompletionError] = useState<string | null>(null);
  const [showExitPrompt, setShowExitPrompt] = useState(false);
  const [signatureAcknowledged, setSignatureAcknowledged] = useState(false);
  const [capturedSignature, setCapturedSignature] = useState<string | null>(null);
  // Mirror of capturedSignature for synchronous reads inside async completion flows,
  // so a signature captured immediately before generation is embedded in the payload.
  const capturedSignatureRef = useRef<string | null>(null);
  const [signatureCaptureOpen, setSignatureCaptureOpen] = useState(false);
  const [customerSummaryOpen, setCustomerSummaryOpen] = useState(false);
  const workflowStartedAtRef = useRef(new Date().toISOString());

  // Document fields modal state
  const [documentFieldsModalOpen, setDocumentFieldsModalOpen] = useState(false);
  // Restricted (buy) program SF 1034 dialog state
  const [restrictedProgramCompleteIssueDialogOpen, setRestrictedProgramCompleteIssueDialogOpen] =
    useState(false);
  const [documentPreviewData, setDocumentPreviewData] = useState<DocumentPreviewResponse | null>(
    null,
  );
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [sizingReviewOpen, setSizingReviewOpen] = useState(false);
  const [pendingSizingCandidates, setPendingSizingCandidates] = useState<
    IssueSizingSelectionCandidate[]
  >([]);
  const [selectedSizingCandidateIds, setSelectedSizingCandidateIds] = useState<string[]>([]);

  // Store pending issue data while document modal is open
  // Pre-issue: stores cart items before completeIssue is called
  // Post-issue: stores result after completeIssue returns
  const pendingIssueDataRef = useRef<{
    // Cart items to issue (set when modal opens)
    buyProgramCartItems: typeof buyProgramTab.cartItems;
    restrictedProgramCartItems: typeof restrictedProgramTab.cartItems;
    ieCartItems: IssuePlannerCartItem[];
    iePlannerItems: IssuePlannerItem[];
    additionalCartItems: IssueAdditionalCartItem[];
    savedSizingSelections: IssueSizingSelection[];
    ieSelections: Array<{ assetId: string; receiptType: 'PHR'; ieKitId?: string }>;
    additionalSelections: Array<{ assetId: string; receiptType: 'THR' }>;
    // Stored form data from DocumentFieldsModal
    documentFormData: Record<string, unknown> | null;
    // Post-issue data (set after completeIssue returns)
    result: {
      memberId: string;
      issuedCount: number;
      receiptIds: string[];
      transactionLogIds: string[];
    } | null;
    documentTransactionId: string | undefined;
    createdBuyProgramAssets: Entity[];
  } | null>(null);

  // SF 1034 digital signature (#510): captured BEFORE generation so it is embedded
  // in the document payload. The dialog gates the CORE Buy Program "Create Document"
  // action; the pending payload is held until the user signs or skips.
  const [sf1034SignOpen, setSf1034SignOpen] = useState(false);
  const pendingBuyProgramPayloadRef = useRef<Record<string, unknown> | null>(null);

  // Restricted (buy) program: confirm whether to email the SF 1034 to the
  // service member before generation. Either choice generates; only "Skip
  // Document" skips.
  const [restrictedProgramSendToSmOpen, setRestrictedProgramSendToSmOpen] = useState(false);
  const pendingRestrictedProgramPayloadRef = useRef<Record<string, unknown> | null>(null);

  const [ieReservedForAdditional, setIeReservedForAdditional] = useState<string[]>([]);
  const [additionalReservedForIe, setAdditionalReservedForIe] = useState<string[]>([]);

  const ieTab = useIssueIeTab({
    open,
    active: !isRestrictedProgramAssignOnly && activeTab === 0,
    customerId,
    externalReservedAssetIds: additionalReservedForIe,
    onReservedAssetIdsChange: setIeReservedForAdditional,
    includeOwnReservedInAvailability: fromSavedCart,
    preferBestQuantityLocationDefault: fromSavedCart,
    readOnly: isCartLocked,
  });
  const additionalTab = useIssueAdditionalItemsTab({
    open,
    active: !isRestrictedProgramAssignOnly && activeTab === 1,
    customerId,
    externalReservedAssetIds: ieReservedForAdditional,
    onReservedAssetIdsChange: setAdditionalReservedForIe,
    readOnly: isCartLocked,
  });
  const buyProgramTab = useIssueBuyProgramTab({
    open,
    active: !isRestrictedProgramAssignOnly && activeTab === 2,
    customerId,
    readOnly: isCartLocked,
  });
  const restrictedProgramTab = useIssueRestrictedProgramTab({
    open,
    active: isRestrictedProgramAssignOnly ? activeTab === 0 : activeTab === 3,
    customerId,
    readOnly: isCartLocked,
  });
  const { data: allCatalogChildren = [] } = useAllCatalogChildren({ enabled: open });
  const { data: existingPersonSizing = [] } = useEntities({
    type: 'person_sizing',
    filters: customerId ? { 'entity_data->>person_id': `eq.${customerId}` } : undefined,
    limit: 500,
    enabled: open && !!customerId,
  });
  const signaturePreviewSrc = useMemo(
    () => (capturedSignature ? `data:image/png;base64,${capturedSignature}` : undefined),
    [capturedSignature],
  );

  useEffect(() => {
    const missingIds = placeholders
      .map((placeholder) => placeholder.parent_catalog_id)
      .filter((id: string) => id && !catalogLookup[id]);

    if (!missingIds.length) {
      return;
    }

    let cancelled = false;

    const loadCatalogs = async () => {
      try {
        const entities = await Promise.all(
          missingIds.map((id) => entityService.get({ type: 'catalog', id })),
        );

        if (cancelled) return;

        setCatalogLookup((prev) => {
          const next = { ...prev };
          entities.forEach((entity: Entity | null, index: number) => {
            const parentId = missingIds[index];
            if (!entity || !parentId) {
              return;
            }
            const data = (entity.entity_data ?? {}) as Record<string, unknown>;
            const lin =
              typeof data.lin === 'string' && data.lin.trim().length > 0 ? data.lin.trim() : null;
            const nsn =
              typeof data.nsn === 'string' && data.nsn.trim().length > 0 ? data.nsn.trim() : null;
            const title =
              entity.display_name?.trim() ||
              (typeof data.name === 'string' ? data.name.trim() : '') ||
              (typeof data.item_name === 'string' ? data.item_name.trim() : '') ||
              `Catalog ${parentId.slice(0, 8)}`;

            next[parentId] = { title, lin: lin ?? undefined, nsn: nsn ?? undefined };
          });
          return next;
        });
      } catch (error) {
        log.error('Failed to load catalog info for requests', { error: String(error) });
      }
    };

    void loadCatalogs();

    return () => {
      cancelled = true;
    };
  }, [placeholders, catalogLookup]);

  useEffect(() => {
    if (!open) {
      setActiveTab(0);
      setSignatureAcknowledged(false);
      setCapturedSignature(null);
      capturedSignatureRef.current = null;
      setSf1034SignOpen(false);
      pendingBuyProgramPayloadRef.current = null;
      setRestrictedProgramSendToSmOpen(false);
      pendingRestrictedProgramPayloadRef.current = null;
      setSignatureCaptureOpen(false);
      setCustomerSummaryOpen(false);
      setDocumentFieldsModalOpen(false);
      setDocumentPreviewData(null);
      setIsLoadingPreview(false);
      setIsGeneratingPdf(false);
      setSizingReviewOpen(false);
      setPendingSizingCandidates([]);
      setSelectedSizingCandidateIds([]);
      setCompletionError(null);
      pendingIssueDataRef.current = null;
    }
  }, [open, isRestrictedProgramAssignOnly]);

  // Guard: when role switches to restricted-program-assign-only while the modal
  // is open (e.g., async role reload), ensure the active tab is valid for the
  // rendered tab set. Resetting to 0 is safe in both directions — tab 0 is
  // always the first visible tab.
  useEffect(() => {
    if (!open) return;
    if (isRestrictedProgramAssignOnly && activeTab !== 0) {
      setActiveTab(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRestrictedProgramAssignOnly, open]);

  const handleCancelSync = async () => {
    const cartId = cartData?.cart?.id;
    if (!cartId) return;

    try {
      await cartService.cancelSync(cartId);
      await queryClient.invalidateQueries({ queryKey: cartQueryKeys.cart(customerId) });
      notifications.success('Sync cancelled', 'The scan session has been cancelled.');
    } catch (error) {
      log.error('Failed to cancel sync', { error: String(error) });
      notifications.error('Cancel failed', 'Could not cancel the scan session.');
    }
  };

  const handleSyncToScanner = async () => {
    const cartId = cartData?.cart?.id;
    if (!cartId) {
      notifications.error('Sync failed', 'No active cart found for this customer.');
      return;
    }

    try {
      await cartService.updateScanningStatus(cartId, ScanningStatus.ReadyToScan);
      await queryClient.invalidateQueries({ queryKey: cartQueryKeys.cart(customerId) });
      notifications.success('Synced to scanner', 'Items are now ready to scan on mobile.');
    } catch (error) {
      log.error('Failed to sync to scanner', { error: String(error) });
      notifications.error('Sync failed', 'Could not update scanning status.');
    }
  };

  const handleCaptureSignature = () => {
    setSignatureCaptureOpen(true);
  };

  const handleSignatureSaved = (signatureBase64Png: string) => {
    setCapturedSignature(signatureBase64Png);
    capturedSignatureRef.current = signatureBase64Png;
    setSignatureAcknowledged(true);
    setSignatureCaptureOpen(false);
    notifications.success('Signature captured', `${person.label} signature saved.`);
  };

  const handleClearSignature = () => {
    setCapturedSignature(null);
    capturedSignatureRef.current = null;
    setSignatureAcknowledged(false);
  };

  const handleSkipSignature = () => {
    setCapturedSignature(null);
    capturedSignatureRef.current = null;
    setSignatureAcknowledged(true);
  };

  const handleRemovePlaceholder = useCallback(
    async (_placeholderId: string, parentCatalogId: string) => {
      if (!customerId) return;
      try {
        // Setting quantity to 0 removes the placeholder
        await upsertSizeAllocationMutation.mutateAsync({
          customerId,
          parentCatalogId,
          quantity: 0,
        });
      } catch (error) {
        const message = extractErrorMessage(error, 'Unable to remove placeholder.');
        notifications.error('Failed to remove placeholder', message);
      }
    },
    [customerId, notifications, upsertSizeAllocationMutation],
  );

  // Helper to complete issue cleanup and finalization
  const completeIssueCleanup = useCallback(
    async (
      result: {
        memberId: string;
        issuedCount: number;
        receiptIds: string[];
        transactionLogIds: string[];
      },
      createdBuyProgramAssets: Entity[],
    ) => {
      createdBuyProgramAssets.length = 0;
      ieTab.actions.clearCart();
      additionalTab.actions.clearCart();
      buyProgramTab.actions.clear();
      restrictedProgramTab.actions.clear();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['ie-summary', customerId] }),
        queryClient.invalidateQueries({ queryKey: ['phr-items', customerId] }),
        queryClient.invalidateQueries({ queryKey: ['thr-items', customerId] }),
      ]);
      notifications.success(
        'Issue completed',
        `Issued ${result.issuedCount} asset${result.issuedCount === 1 ? '' : 's'}.`,
      );
      setSignatureAcknowledged(false);
      setCapturedSignature(null);
      capturedSignatureRef.current = null;
      onCompleted?.({
        memberId: result.memberId,
        issuedCount: result.issuedCount,
        receiptIds: result.receiptIds,
        transactionLogIds: Array.isArray(result.transactionLogIds) ? result.transactionLogIds : [],
      });
      pendingIssueDataRef.current = null;
      onClose();
    },
    [
      customerId,
      queryClient,
      notifications,
      ieTab.actions,
      additionalTab.actions,
      buyProgramTab.actions,
      restrictedProgramTab.actions,
      onCompleted,
      onClose,
    ],
  );

  const showIssueWarnings = useCallback(
    (warnings: string[] | undefined) => {
      if (!Array.isArray(warnings) || warnings.length === 0) {
        return;
      }

      notifications.warning('Issue completed with warnings', warnings.join(' '));
    },
    [notifications],
  );

  const sizingCandidateKeys = useMemo(
    () =>
      pendingSizingCandidates.map(
        (candidate) => `${candidate.parentCatalogId}::${candidate.catalogId}`,
      ),
    [pendingSizingCandidates],
  );

  const buildSavedSizingSelections = useCallback(
    (selectedKeys: string[]): IssueSizingSelection[] => {
      const selectedKeySet = new Set(selectedKeys);
      return pendingSizingCandidates
        .filter((candidate) =>
          selectedKeySet.has(`${candidate.parentCatalogId}::${candidate.catalogId}`),
        )
        .map((candidate) => ({
          parentCatalogId: candidate.parentCatalogId,
          catalogId: candidate.catalogId,
          itemName: candidate.itemName,
          ...(candidate.sizeLabel ? { sizeLabel: candidate.sizeLabel } : {}),
        }));
    },
    [pendingSizingCandidates],
  );

  // Build the initial payload for document preview (buy program format)
  const buildBuyProgramPayload = useCallback(
    (
      buyProgramCartItems: typeof buyProgramTab.cartItems,
      documentTransactionId: string | undefined,
    ) => {
      const pdfItems: PdfFormItem[] = buyProgramCartItems.map((item) => ({
        name: item.catalog.name,
        lin: item.catalog.lin || undefined,
        nsn: item.catalog.nsn || undefined,
        quantity: item.quantity || 1,
        unitPrice: item.actualPrice || 0, // Use actual price from cart, not ceiling price
        tax: item.tax || 0, // Include tax from cart
        shipping: item.shipping || 0, // Include shipping from cart
        loa: item.catalog.loa || undefined,
        receiptDate: item.receiptDate || undefined, // Per-item receipt date (#458)
      }));

      return {
        documentId: '1034',
        name: memberName || '',
        payeeSsn: customer?.ssn?.trim() || '',
        payeeSquadronSection: customer?.unit || '',
        items: pdfItems,
        certifyingOfficer: {
          name: undefined,
          title: undefined,
        },
        organization: {
          name: unitName,
          location: unitName,
        },
        transactionInfo: {
          id: documentTransactionId,
          timestamp: new Date().toISOString(),
        },
        remarks: [
          'Buy Program Purchase - Issue Workflow',
          user?.email ? `Issued by: ${user.email}` : null,
          `Items: ${pdfItems.length}`,
        ]
          .filter(Boolean)
          .join('\n'),
        signatures: capturedSignature ? { individual: capturedSignature } : undefined,
      };
    },
    // buyProgramTab is only used for type inference in the parameter, not accessed at runtime
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [memberName, unitName, user, capturedSignature],
  );

  /**
   * Build ChangeDocumentLineItem[] from all cart types and generate the Change Document PDF.
   *
   * Consolidates the duplicated logic for building line items from IE Kit, additional (THR),
   * and Buy Program cart items, then calling generateChangeDocument. Used by all three
   * issue completion paths: executeIssueCompletion (Buy Program) and handleCompleteIssue (standard).
   *
   * @returns The result from generateChangeDocument, or null if no items or generation failed.
   */
  const buildAndGenerateChangeDocument = useCallback(
    async (params: {
      preOpenedWindow?: Window | null;
      signature?: string;
      documentNumber: string;
      transactionLogId?: string;
      ieCartItems: IssuePlannerCartItem[];
      iePlannerItems: IssuePlannerItem[];
      additionalCartItems: IssueAdditionalCartItem[];
      buyProgramCartItems: IssueBuyProgramCartItem[];
    }) => {
      const {
        preOpenedWindow,
        signature,
        documentNumber,
        transactionLogId,
        ieCartItems,
        iePlannerItems,
        additionalCartItems,
        buyProgramCartItems: bpCartItems,
      } = params;

      if (
        ieCartItems.length === 0 &&
        additionalCartItems.length === 0 &&
        bpCartItems.length === 0
      ) {
        if (preOpenedWindow && !preOpenedWindow.closed) preOpenedWindow.close();
        return null;
      }

      const changeDocItems: ChangeDocumentLineItem[] = [];

      // --- Fetch customer's current hand receipt items for QTY OH ---
      const qtyOnHandByLin = new Map<string, number>();
      if (customerId) {
        try {
          const [phrItems, thrItems] = await Promise.all([
            iemService.getPHRItems(customerId, { status: 'neq.closed' }),
            iemService.getTHRItems(customerId, { status: 'neq.closed' }),
          ]);
          for (const phr of phrItems) {
            const lin = phr.lin ?? '';
            if (lin) {
              qtyOnHandByLin.set(lin, (qtyOnHandByLin.get(lin) ?? 0) + 1);
            }
          }
          for (const thr of thrItems) {
            const lin = thr.lin ?? '';
            if (lin) {
              qtyOnHandByLin.set(lin, (qtyOnHandByLin.get(lin) ?? 0) + 1);
            }
          }
        } catch (err) {
          log.warn('Failed to fetch hand receipt items for QTY OH', { error: String(err) });
        }
      }

      // --- Fetch asset & catalog data for Unit/Total Cost, and R/NR ---
      const allAssetIds = [
        ...ieCartItems.flatMap((item) => item.assetIds),
        ...additionalCartItems.flatMap((item) => item.assetIds),
      ];

      const catalogIdByAssetId = new Map<string, string | null>();
      const collectedCatalogIds: string[] = [];

      // Batch-fetch asset entities
      for (let i = 0; i < allAssetIds.length; i += 75) {
        const batchIds = allAssetIds.slice(i, i + 75);
        if (batchIds.length === 0) continue;
        try {
          const idFilter =
            batchIds.length === 1 ? `eq.${batchIds[0]}` : `in.(${batchIds.join(',')})`;
          const assets = await entityService.list({
            type: 'asset',
            limit: batchIds.length,
            order: 'id.asc',
            filters: { id: idFilter, select: 'id,entity_data' },
          });
          for (const asset of assets) {
            const ed = (asset.entity_data ?? {}) as Record<string, unknown>;
            const rawCatalogId = ed.catalog_id;
            const catId =
              typeof rawCatalogId === 'string' && rawCatalogId.trim().length > 0
                ? rawCatalogId.trim()
                : null;
            catalogIdByAssetId.set(asset.id, catId);
            if (catId) collectedCatalogIds.push(catId);
          }
        } catch (err) {
          log.warn('Failed to fetch asset entities for Change Document enrichment', {
            error: String(err),
          });
        }
      }

      // Also gather catalog IDs from planner items and BP items
      for (const item of ieCartItems) {
        const plannerItem = iePlannerItems.find((p) => p.id === item.plannerItemId);
        const catId = item.selectedCatalogId || plannerItem?.catalogId;
        if (catId) collectedCatalogIds.push(catId);
      }
      for (const item of bpCartItems) {
        if (item.catalog?.id) collectedCatalogIds.push(item.catalog.id);
      }

      const unitCostByCatalogId = new Map<string, number | null>();
      const recoverableByCatalogId = new Map<string, string | null>();

      const uniqueCatalogIds = Array.from(new Set(collectedCatalogIds));
      for (let i = 0; i < uniqueCatalogIds.length; i += 75) {
        const batchIds = uniqueCatalogIds.slice(i, i + 75);
        if (batchIds.length === 0) continue;
        try {
          const idFilter =
            batchIds.length === 1 ? `eq.${batchIds[0]}` : `in.(${batchIds.join(',')})`;
          const catalogs = await entityService.list({
            type: 'catalog',
            limit: batchIds.length,
            order: 'id.asc',
            filters: { id: idFilter, select: 'id,entity_data' },
          });
          for (const catalog of catalogs) {
            const ed = (catalog.entity_data ?? {}) as Record<string, unknown>;
            const rawRecoverable = ed.recoverable_nonrecoverable;
            if (typeof rawRecoverable === 'string') {
              const norm = rawRecoverable.trim().toUpperCase();
              recoverableByCatalogId.set(
                catalog.id,
                norm.includes('NON')
                  ? 'NR'
                  : norm.includes('RECOVERABLE')
                    ? 'R'
                    : rawRecoverable.trim(),
              );
            } else {
              recoverableByCatalogId.set(catalog.id, null);
            }

            const rawPrice = ed.unit_price ?? ed.price ?? ed.cost;
            const price =
              typeof rawPrice === 'number'
                ? rawPrice
                : typeof rawPrice === 'string'
                  ? Number.parseFloat(rawPrice)
                  : null;
            unitCostByCatalogId.set(
              catalog.id,
              typeof price === 'number' && Number.isFinite(price) ? price : null,
            );
          }
        } catch (err) {
          log.warn('Failed to fetch catalog entities for Change Document enrichment', {
            error: String(err),
          });
        }
      }

      /** Resolve the catalog ID for a cart item using asset lookup or planner fallback. */
      const resolveCatalogId = (
        assetIds: string[],
        plannerCatalogId?: string | null,
      ): string | null => {
        for (const aid of assetIds) {
          const catId = catalogIdByAssetId.get(aid);
          if (catId) return catId;
        }
        return plannerCatalogId ?? null;
      };

      /** Resolve the QTY OH for a line item by counting matching LIN on the customer's hand receipt. */
      const resolveQtyOnHand = (lin: string): string => {
        if (!lin) return '';
        const count = qtyOnHandByLin.get(lin);
        return typeof count === 'number' ? String(count) : '';
      };

      const buildCostFields = (
        catalogId: string | null,
        qty: number,
      ): { unit_cost: string; total_cost: string; r_nr: string } => {
        const unitCost = catalogId ? (unitCostByCatalogId.get(catalogId) ?? null) : null;
        const rNr = catalogId ? (recoverableByCatalogId.get(catalogId) ?? null) : null;
        return {
          unit_cost: typeof unitCost === 'number' ? formatCurrency(unitCost) : '',
          total_cost: typeof unitCost === 'number' ? formatCurrency(unitCost * qty) : '',
          r_nr: rNr ?? '',
        };
      };

      const buildFallbackNameFromEntity = (entity: Entity): string => {
        const entityData = entity.entity_data as Record<string, unknown>;
        const firstName =
          typeof entityData.first_name === 'string' ? entityData.first_name.trim() : '';
        const lastName =
          typeof entityData.last_name === 'string' ? entityData.last_name.trim() : '';
        const fullName = [lastName, firstName].filter(Boolean).join(', ').trim();
        return fullName || entity.display_name || '';
      };

      const resolveCustomerDocumentFields = async (): Promise<{
        name: string;
        individualId: string;
        rank: string;
        section: string;
      }> => {
        const directName =
          (customer
            ? [customer.last_name, customer.first_name].filter(Boolean).join(', ').trim()
            : null) ||
          memberName ||
          person.label;

        const directIndividualId = customer?.individual_id?.trim() || '';
        const directRank = customer?.rank?.trim() || '';
        const directSection = (customer?.unit || unitName || '').trim();

        if (directIndividualId && directRank && directSection) {
          return {
            name: directName,
            individualId: directIndividualId,
            rank: directRank,
            section: directSection,
          };
        }

        if (!customerId) {
          return {
            name: directName,
            individualId: directIndividualId,
            rank: directRank,
            section: directSection,
          };
        }

        try {
          const customerEntity = await entityService.getEntity(customerId, 'person');
          if (!customerEntity) {
            return {
              name: directName,
              individualId: directIndividualId,
              rank: directRank,
              section: directSection,
            };
          }

          const entityData = customerEntity.entity_data as Record<string, unknown>;
          const entityMetadata = (customerEntity.metadata ?? {}) as Record<string, unknown>;
          const resolveEntityField = (key: 'individual_id' | 'rank' | 'unit'): string => {
            const dataValue =
              typeof entityData[key] === 'string' ? (entityData[key] as string).trim() : '';
            if (dataValue) {
              return dataValue;
            }

            const metadataValue =
              typeof entityMetadata[key] === 'string' ? (entityMetadata[key] as string).trim() : '';
            return metadataValue;
          };

          const fallbackIndividualId = resolveEntityField('individual_id');
          const fallbackRank = resolveEntityField('rank');
          const fallbackSection = resolveEntityField('unit');
          const fallbackName = buildFallbackNameFromEntity(customerEntity);

          return {
            name: directName || fallbackName || person.label,
            individualId: directIndividualId || fallbackIndividualId,
            rank: directRank || fallbackRank,
            section: directSection || fallbackSection,
          };
        } catch (error) {
          log.warn('Unable to resolve fallback customer data for Change Document', {
            customerId,
            error: String(error),
          });
          return {
            name: directName,
            individualId: directIndividualId,
            rank: directRank,
            section: directSection,
          };
        }
      };

      for (const item of ieCartItems) {
        const plannerItem = iePlannerItems.find((p) => p.id === item.plannerItemId);
        const catalogId = resolveCatalogId(
          item.assetIds,
          item.selectedCatalogId || plannerItem?.catalogId,
        );
        const qty = item.assetIds.length;
        const costFields = buildCostFields(catalogId, qty);
        changeDocItems.push({
          qty_issued: String(qty),
          lin: item.lin || '',
          nsn: item.nsn || plannerItem?.nsn || '',
          nomenclature: formatNomenclatureWithSize(item.name, item.sizeLabel),
          thr_phr: 'PHR',
          due_date: '',
          qty_oh: resolveQtyOnHand(item.lin || ''),
          unit_cost: costFields.unit_cost,
          total_cost: costFields.total_cost,
          r_nr: costFields.r_nr,
        });
      }

      // THR additional items get a 90-day due-back date
      const dueBackDate = new Date();
      dueBackDate.setDate(dueBackDate.getDate() + 90);
      const dueBackDateStr = dueBackDate.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });

      for (const item of additionalCartItems) {
        const catalogId = resolveCatalogId(item.assetIds, null);
        const qty = item.assetIds.length;
        const costFields = buildCostFields(catalogId, qty);
        changeDocItems.push({
          qty_issued: String(qty),
          lin: item.lin || '',
          nsn: item.nsn || '',
          nomenclature: formatNomenclatureWithSize(item.name, item.sizeLabel),
          thr_phr: 'THR',
          due_date: dueBackDateStr,
          qty_oh: resolveQtyOnHand(item.lin || ''),
          unit_cost: costFields.unit_cost,
          total_cost: costFields.total_cost,
          r_nr: costFields.r_nr,
        });
      }

      for (const item of bpCartItems) {
        const catalog = item.catalog;
        const unitCost = item.actualPrice || catalog.unitPrice || 0;
        const qty = item.quantity || 1;
        const catalogId = catalog.id;
        const rNr = catalogId ? (recoverableByCatalogId.get(catalogId) ?? '') : '';
        changeDocItems.push({
          qty_issued: String(qty),
          lin: catalog.lin || '',
          nsn: catalog.nsn || '',
          nomenclature: catalog.name,
          thr_phr: 'PHR',
          due_date: '',
          qty_oh: '',
          unit_cost: unitCost ? formatCurrency(unitCost) : '',
          total_cost: unitCost ? formatCurrency(unitCost * qty) : '',
          r_nr: rNr,
        });
      }

      const resolvedCustomer = await resolveCustomerDocumentFields();

      const changeDocResult = await generateChangeDocument({
        documentType: 'ChangeDocumentIssue',
        items: changeDocItems,
        name: resolvedCustomer.name,
        individualId: resolvedCustomer.individualId,
        rank: resolvedCustomer.rank,
        section: resolvedCustomer.section,
        documentNumber,
        transactionId: transactionLogId,
        signature: signature || undefined,
        preOpenedWindow,
      });

      // Link the generated Change Document to the transaction log entry
      if (changeDocResult?.documentEntityId && transactionLogId) {
        try {
          await documentService.linkDocumentToTransaction(
            changeDocResult.documentEntityId,
            transactionLogId,
          );
        } catch (linkError) {
          log.error('Failed to link Change Document to transaction', {
            error: String(linkError),
          });
        }
      }

      return changeDocResult;
    },
    [customer, customerId, memberName, unitName],
  );

  // Execute Buy Program issue completion, with or without SF 1034 document generation.
  // Called from DocumentFieldsModal's onConfirm (with editedPayload) or onSkip (without).
  const executeIssueCompletion = useCallback(
    async (editedPayload?: Record<string, unknown>) => {
      if (!pendingIssueDataRef.current || !customerId) {
        log.error('No pending issue data or customer ID');
        return;
      }

      const {
        buyProgramCartItems,
        restrictedProgramCartItems,
        ieSelections,
        additionalSelections,
        ieCartItems,
        iePlannerItems,
        additionalCartItems,
        savedSizingSelections,
      } = pendingIssueDataRef.current;
      const withSF1034 = !!editedPayload;

      if (editedPayload) {
        pendingIssueDataRef.current.documentFormData = editedPayload;
      }

      setIsGeneratingPdf(true);
      let shouldResetDocumentModalState = false;
      const createdBuyProgramAssets: Entity[] = [];
      pendingIssueDataRef.current.createdBuyProgramAssets = createdBuyProgramAssets;
      setCompletionError(null);

      // Pre-open windows SYNCHRONOUSLY (before any await) while we have user-gesture context.
      // Browsers block window.open after async operations lose the click context.
      // Only open SF 1034 window when generating the document.
      const sf1034Window = withSF1034 ? preOpenWindow() : null;
      const changeDocWindow = preOpenWindow();

      const closeAllWindows = () => {
        for (const win of [sf1034Window, changeDocWindow]) {
          closeIfOpen(win);
        }
      };

      const rollbackCreatedBuyProgramAssets = async () => {
        if (createdBuyProgramAssets.length === 0) return;
        const assetIds = createdBuyProgramAssets.map((asset) => asset.id);
        const rollbackResults = await Promise.allSettled(
          assetIds.map((assetId) => entityService.deleteEntity(assetId)),
        );
        const failedRollbacks = rollbackResults.filter((result) => result.status === 'rejected');
        if (failedRollbacks.length > 0) {
          log.error('Failed to rollback buy program assets', { assetIds });
        }
        createdBuyProgramAssets.length = 0;
      };

      try {
        // Resolve actor: prefer oid from the live access token (Azure Workforce oid is a UUID;
        // sub is a pairwise opaque string that cannot be cast to uuid by Postgres).
        const accessToken = useAuthStore.getState().accessToken;
        const actorId =
          (accessToken ? getActorIdFromToken(accessToken) : null) ??
          (typeof user?.id === 'string' && isUuid(user.id) ? user.id : undefined);

        // 1. Create Buy Program assets
        const buyProgramAssetKitIds = new Map<string, string | undefined>();
        for (const item of buyProgramCartItems) {
          const qty = item.quantity || 1;
          const kitId = item.ieKitId ?? undefined;
          for (let i = 0; i < qty; i++) {
            const asset = await iemService.createBuyProgramAssetFromCatalog({
              catalogId: item.catalog.id,
              catalogIdentifier: item.catalog.entityIdentifier,
              displayName: item.catalog.displayName ?? item.catalog.name,
              entityData: item.catalog.entityData,
              actorId,
              // Include pricing data for auditing and document regeneration
              actualPrice: item.actualPrice,
              tax: item.tax,
              shipping: item.shipping,
            });
            createdBuyProgramAssets.push(asset);
            buyProgramAssetKitIds.set(asset.id, kitId);
          }
        }

        const buyProgramSelections = createdBuyProgramAssets.map((asset) => ({
          assetId: asset.id,
          receiptType: 'PHR' as const,
          ...(buyProgramAssetKitIds.get(asset.id)
            ? { ieKitId: buyProgramAssetKitIds.get(asset.id) }
            : {}),
        }));

        // 1b. Create restricted (buy) program assets
        const restrictedProgramAssetKitIds = new Map<string, string | undefined>();
        for (const item of restrictedProgramCartItems) {
          const qty = item.quantity || 1;
          const kitId = item.ieKitId ?? undefined;
          for (let i = 0; i < qty; i++) {
            const asset = await iemService.createBuyProgramAssetFromCatalog({
              catalogId: item.catalog.id,
              catalogIdentifier: item.catalog.entityIdentifier,
              displayName: item.catalog.displayName ?? item.catalog.name,
              entityData: item.catalog.entityData,
              actorId,
              actualPrice: item.actualPrice,
              tax: item.tax,
              shipping: item.shipping,
            });
            createdBuyProgramAssets.push(asset);
            restrictedProgramAssetKitIds.set(asset.id, kitId);
          }
        }

        const restrictedProgramSelections = createdBuyProgramAssets
          .filter((a) => restrictedProgramAssetKitIds.has(a.id))
          .map((asset) => ({
            assetId: asset.id,
            receiptType: 'PHR' as const,
            ...(restrictedProgramAssetKitIds.get(asset.id)
              ? { ieKitId: restrictedProgramAssetKitIds.get(asset.id) }
              : {}),
          }));

        const combinedSelections = [
          ...ieSelections,
          ...additionalSelections,
          ...buyProgramSelections,
          ...restrictedProgramSelections,
        ];

        // Dedupe
        const dedupedItems: Array<{
          assetId: string;
          receiptType: 'PHR' | 'THR';
          ieKitId?: string;
        }> = [];
        const seen = new Set<string>();
        for (const entry of combinedSelections) {
          if (!seen.has(entry.assetId)) {
            seen.add(entry.assetId);
            dedupedItems.push(entry);
          }
        }

        if (dedupedItems.length === 0) {
          await rollbackCreatedBuyProgramAssets();
          closeAllWindows();
          notifications.error(
            'No assets selected',
            'Add available assets to the cart before completing the issue.',
          );
          shouldResetDocumentModalState = true;
          return;
        }

        // 2. Build SF 1034 document payload if user provided form data
        const documentPayload = editedPayload
          ? {
              ...editedPayload,
              transactionInfo: {
                ...(editedPayload.transactionInfo as Record<string, unknown> | undefined),
                timestamp: new Date().toISOString(),
              },
              signatures: capturedSignatureRef.current
                ? { individual: capturedSignatureRef.current }
                : (editedPayload.signatures as { individual?: string } | undefined),
            }
          : undefined;

        log.debug(
          withSF1034
            ? 'Calling atomic completeIssue with SF 1034 payload'
            : 'Completing issue WITHOUT SF 1034 (skipped)',
          { itemCount: dedupedItems.length },
        );

        // 3. Complete the issue (with or without atomic SF 1034 generation)
        let result: {
          memberId: string;
          issuedCount: number;
          receiptIds: string[];
          transactionLogIds: string[];
          documentEntityId?: string;
          documentDownloadUrl?: string;
          documentNumber?: string;
          warnings?: string[];
        };

        const buyProgramAssetIdList = createdBuyProgramAssets.map((a) => a.id);

        try {
          result = await iemService.completeIssue({
            memberId: customerId,
            items: dedupedItems,
            quantitySummary: buildQuantitySummary(ieCartItems, iePlannerItems, additionalCartItems),
            savedSizingSelections,
            ...(scanningStatus === ScanningStatus.Complete && { isScannerApp: true }),
            ...(documentPayload && { documentPayload }),
            ...buildBuyProgramRequestFragment(buyProgramAssetIdList),
            startedAt: workflowStartedAtRef.current,
          });
        } catch (issueError) {
          await rollbackCreatedBuyProgramAssets();
          throw issueError;
        }

        pendingIssueDataRef.current.result = result;
        showIssueWarnings(result.warnings);

        // 4. Handle SF 1034 result (only when document was generated atomically)
        if (withSF1034) {
          const documentEntityId = result.documentEntityId ?? null;
          const resolvedUrl: string | null = result.documentDownloadUrl
            ? documentService.resolveDownloadUrl(result.documentDownloadUrl)
            : null;

          if (!resolvedUrl) {
            log.warn('Issue completed but document URL not returned', {
              documentEntityId,
              issuedCount: result.issuedCount,
            });
            notifications.warning(
              'Issue completed',
              `Issued ${result.issuedCount} item${result.issuedCount === 1 ? '' : 's'}. ` +
                'SF 1034 was generated but the download link is temporarily unavailable. Check Transaction Log.',
            );
            closeIfOpen(sf1034Window);
            // Do NOT abort: the Change Document must still generate independently of
            // the SF 1034 outcome. Flow continues to step 5.
          } else {
            await openDocumentWithAuth(resolvedUrl, sf1034Window);

            // If "Create & Email" was chosen, open the member's email client with the document URL
            const payeeEmail =
              typeof editedPayload?.payeeEmail === 'string' ? editedPayload.payeeEmail.trim() : '';
            const shouldSendEmail = editedPayload?.sendEmail === true && payeeEmail.length > 0;
            if (shouldSendEmail) {
              const subject = encodeURIComponent(
                `SF 1034 — ${restrictedProgram.programLabel} Issue`,
              );
              const body = encodeURIComponent(
                `Please find the SF 1034 for your ${restrictedProgram.programLabel} issue attached.\n\nDocument URL: ${resolvedUrl}`,
              );
              window.open(`mailto:${payeeEmail}?subject=${subject}&body=${body}`, '_blank');
            }

            const emailAction = shouldSendEmail ? undefined : payeeEmail ? (
              <Button
                size="small"
                color="inherit"
                onClick={() => {
                  const subject = encodeURIComponent(
                    `SF 1034 — ${restrictedProgram.programLabel} Issue`,
                  );
                  const body = encodeURIComponent(`Document URL: ${resolvedUrl}`);
                  window.open(`mailto:${payeeEmail}?subject=${subject}&body=${body}`, '_blank');
                }}
              >
                Email
              </Button>
            ) : undefined;

            notifications.success(
              'Issue Completed',
              `Issued ${result.issuedCount} item${result.issuedCount === 1 ? '' : 's'}.${documentEntityId ? ' SF 1034 saved to Documents.' : ' SF 1034 generated.'}`,
              emailAction ? { action: emailAction, duration: 10000 } : undefined,
            );
          }
        } else {
          // SF 1034 was skipped — save the payload so it can be generated later
          const transactionLogId = result.transactionLogIds?.[0] ?? null;
          try {
            const skippedPayload = buildBuyProgramPayload(
              pendingIssueDataRef.current.buyProgramCartItems,
              transactionLogId ?? undefined,
            );
            await documentService.saveDocumentPayload({
              documentType: 'SF1034',
              payload: skippedPayload,
              transactionId: transactionLogId,
              documentNumber: result.documentNumber ?? null,
            });
          } catch {
            // Best-effort — issue is already committed
          }

          notifications.success(
            'Issue completed',
            `Issued ${result.issuedCount} item${result.issuedCount === 1 ? '' : 's'}. ` +
              'SF 1034 was skipped - you can upload a document from the Transaction Log.',
          );
        }

        // 5. Generate Change Document
        // DA3161 is generated automatically by the backend workflow (AB#29323).
        const txLogId = result.transactionLogIds?.[0];

        try {
          const changeDocResult = await buildAndGenerateChangeDocument({
            preOpenedWindow: changeDocWindow,
            signature: capturedSignatureRef.current || undefined,
            documentNumber: result.documentNumber || '',
            transactionLogId: txLogId,
            ieCartItems,
            iePlannerItems,
            additionalCartItems,
            buyProgramCartItems,
          });

          if (!changeDocResult) {
            notifications.warning('Document Notice', 'Change Document could not be generated.');
          }
        } catch (changeDocError) {
          log.error('Failed to generate Change Document', { error: String(changeDocError) });
          closeIfOpen(changeDocWindow);
          notifications.warning('Document Notice', 'Change Document could not be generated.');
        }

        // 7. Clean up and finalize.
        // The SF 1034 digital signature is now captured BEFORE generation and
        // embedded in the document payload, so no post-generation signature step
        // is required here.
        await completeIssueCleanup(result, createdBuyProgramAssets);
        shouldResetDocumentModalState = true;
      } catch (error) {
        closeAllWindows();
        await rollbackCreatedBuyProgramAssets();
        const message = extractErrorMessage(error, 'Unexpected error completing issue.');
        notifications.error('Failed to complete issue', message, { duration: 10000 });
        setCompletionError(message);
        log.error('Failed to complete issue', { error: String(error) });
      } finally {
        setIsGeneratingPdf(false);
        // Preserve the SF 1034 form state when document generation fails so users can retry
        if (shouldResetDocumentModalState) {
          setDocumentFieldsModalOpen(false);
          setDocumentPreviewData(null);
          pendingIssueDataRef.current = null;
        }
      }
    },
    [
      customerId,
      user,
      memberName,
      scanningStatus,
      notifications,
      completeIssueCleanup,
      capturedSignature,
      buildAndGenerateChangeDocument,
      restrictedProgram.programLabel,
    ],
  );

  // Handle document fields modal close (cancel)
  // User can cancel to go back and adjust the cart before completing
  const handleDocumentFieldsModalClose = useCallback(() => {
    if (isGeneratingPdf) return; // Don't allow closing while in progress

    // Clear pending data and close modal - user can adjust cart and try again
    pendingIssueDataRef.current = null;
    setDocumentFieldsModalOpen(false);
    setDocumentPreviewData(null);
  }, [isGeneratingPdf]);

  const continueIssueCompletion = useCallback(
    async (savedSizingSelections: IssueSizingSelection[]) => {
      setCompletionError(null);
      if (!customerId) {
        notifications.error(
          'Unable to complete issue',
          `Select ${person.article} ${person.lower} before issuing equipment.`,
        );
        return;
      }

      const plannerSelections = ieTab.cartItems.flatMap((item) =>
        item.assetIds.map((assetId) => ({
          assetId,
          receiptType: 'PHR' as const,
          ieKitId: item.ieKitId ?? undefined,
        })),
      );

      const additionalSelections = additionalTab.cartItems.flatMap((item) =>
        item.assetIds.map((assetId) => ({
          assetId,
          receiptType: 'THR' as const,
        })),
      );

      const buyProgramCartItems = buyProgramTab.cartItems;
      const restrictedProgramCartItems = restrictedProgramTab.cartItems;
      const hasBuyProgramIssuance = buyProgramCartItems.length > 0;

      if (
        plannerSelections.length === 0 &&
        additionalSelections.length === 0 &&
        buyProgramCartItems.length === 0 &&
        restrictedProgramCartItems.length === 0
      ) {
        notifications.warning(
          'Cart is empty',
          'Add assets to the cart before completing the issue.',
        );
        return;
      }

      if (hasBuyProgramIssuance) {
        if (!memberName?.trim()) {
          notifications.error(
            'Member name required',
            `Cannot generate SF 1034 without a member name. Please select ${person.article} ${person.lower}.`,
          );
          return;
        }

        log.debug('Opening SF 1034 modal BEFORE issue completion', {
          memberName,
          itemCount: buyProgramCartItems.length,
        });

        pendingIssueDataRef.current = {
          buyProgramCartItems,
          restrictedProgramCartItems,
          ieCartItems: ieTab.cartItems,
          iePlannerItems: ieTab.plannerItems,
          additionalCartItems: additionalTab.cartItems,
          savedSizingSelections,
          ieSelections: plannerSelections,
          additionalSelections,
          documentFormData: null,
          result: null,
          documentTransactionId: undefined,
          createdBuyProgramAssets: [],
        };

        const initialPayload = buildBuyProgramPayload(buyProgramCartItems, undefined);

        try {
          setIsLoadingPreview(true);
          const previewResponse = await documentService.getDocumentPreview({
            documentType: 'SF1034',
            payload: initialPayload,
          });

          setDocumentPreviewData(previewResponse);
          setDocumentFieldsModalOpen(true);
        } catch (previewError) {
          log.error('Failed to load document preview', { error: String(previewError) });
          notifications.error(
            'Unable to load SF 1034 form',
            'Could not load the document fields. Please try again.',
          );
          pendingIssueDataRef.current = null;
        } finally {
          setIsLoadingPreview(false);
        }
        return;
      }

      // Restricted (buy) program path: open the static SF 1034 dialog before completing
      const hasRestrictedProgramIssuance = restrictedProgramCartItems.length > 0;
      if (hasRestrictedProgramIssuance) {
        log.debug('Opening restricted program SF 1034 dialog BEFORE issue completion', {
          memberName,
          itemCount: restrictedProgramCartItems.length,
        });

        pendingIssueDataRef.current = {
          buyProgramCartItems,
          restrictedProgramCartItems,
          ieCartItems: ieTab.cartItems,
          iePlannerItems: ieTab.plannerItems,
          additionalCartItems: additionalTab.cartItems,
          savedSizingSelections,
          ieSelections: plannerSelections,
          additionalSelections,
          documentFormData: null,
          result: null,
          documentTransactionId: undefined,
          createdBuyProgramAssets: [],
        };

        setRestrictedProgramCompleteIssueDialogOpen(true);
        return;
      }

      const createdBuyProgramAssets: Entity[] = [];
      const stdChangeDocWindow = preOpenWindow();

      try {
        setIsCompleting(true);
        setCompletionError(null);

        const combinedSelections = [...plannerSelections, ...additionalSelections];

        if (combinedSelections.length === 0) {
          closeIfOpen(stdChangeDocWindow);
          notifications.error(
            'No assets selected',
            'Add available assets to the cart before completing the issue.',
          );
          return;
        }

        const dedupedItems: Array<{
          assetId: string;
          receiptType: 'PHR' | 'THR';
          ieKitId?: string;
        }> = [];
        const seen = new Set<string>();

        for (const entry of combinedSelections) {
          if (!seen.has(entry.assetId)) {
            seen.add(entry.assetId);
            dedupedItems.push(entry);
          }
        }

        if (dedupedItems.length === 0) {
          closeIfOpen(stdChangeDocWindow);
          notifications.error(
            'No assets selected',
            'Add available assets to the cart before completing the issue.',
          );
          return;
        }

        const invalidAsset = dedupedItems.find((entry) => !isUuid(entry.assetId));
        if (invalidAsset) {
          closeIfOpen(stdChangeDocWindow);
          notifications.error(
            'Invalid asset detected',
            'One or more selected assets could not be validated. Remove them and try again.',
          );
          return;
        }

        const result = await iemService.completeIssue({
          memberId: customerId,
          items: dedupedItems,
          quantitySummary: buildQuantitySummary(
            ieTab.cartItems,
            ieTab.plannerItems,
            additionalTab.cartItems,
          ),
          savedSizingSelections,
          ...(scanningStatus === ScanningStatus.Complete && { isScannerApp: true }),
          startedAt: workflowStartedAtRef.current,
        });
        showIssueWarnings(result.warnings);

        try {
          const changeDocResult = await buildAndGenerateChangeDocument({
            preOpenedWindow: stdChangeDocWindow,
            signature: capturedSignatureRef.current || undefined,
            documentNumber: result.documentNumber || '',
            transactionLogId: result.transactionLogIds?.[0],
            ieCartItems: ieTab.cartItems,
            iePlannerItems: ieTab.plannerItems,
            additionalCartItems: additionalTab.cartItems,
            buyProgramCartItems: [],
          });

          if (!changeDocResult) {
            notifications.warning('Document Notice', 'Change Document could not be generated.');
          }
        } catch (docError) {
          log.error('Failed to generate Change Document', { error: String(docError) });
          closeIfOpen(stdChangeDocWindow);
          notifications.warning('Document Notice', 'Change Document could not be generated.');
        }

        log.info('Issue completed successfully (no Buy Program)', {
          transactionLogIds: result.transactionLogIds,
        });
        await completeIssueCleanup(result, createdBuyProgramAssets);
      } catch (error) {
        closeIfOpen(stdChangeDocWindow);
        const message = extractErrorMessage(error, 'Unexpected error completing issue.');
        notifications.error('Failed to complete issue', message, { duration: 10000 });
        setCompletionError(message);
        log.error('Failed to complete issue', { error: String(error) });
      } finally {
        setIsCompleting(false);
      }
    },
    [
      customerId,
      notifications,
      ieTab.cartItems,
      ieTab.plannerItems,
      additionalTab.cartItems,
      buyProgramTab.cartItems,
      memberName,
      buildBuyProgramPayload,
      scanningStatus,
      showIssueWarnings,
      capturedSignature,
      buildAndGenerateChangeDocument,
      completeIssueCleanup,
    ],
  );

  const handleSizingReviewConfirm = useCallback(async () => {
    const selectedSizingSelections = buildSavedSizingSelections(selectedSizingCandidateIds);
    setSizingReviewOpen(false);
    await continueIssueCompletion(selectedSizingSelections);
  }, [buildSavedSizingSelections, continueIssueCompletion, selectedSizingCandidateIds]);

  const handleCompleteIssue = async () => {
    if (!customerId) {
      notifications.error(
        'Unable to complete issue',
        `Select ${person.article} ${person.lower} before issuing equipment.`,
      );
      return;
    }

    if (restrictedProgramTab.hasIncompleteItems) {
      notifications.error(
        'Unable to complete issue',
        `Enter an actual price and receipt date for every ${restrictedProgram.programLabel} item before completing the issue.`,
      );
      return;
    }

    const buyProgramCartItems = buyProgramTab.cartItems;
    const restrictedProgramCartItems = restrictedProgramTab.cartItems;

    if (
      ieTab.cartItems.length === 0 &&
      additionalTab.cartItems.length === 0 &&
      buyProgramCartItems.length === 0 &&
      restrictedProgramCartItems.length === 0
    ) {
      notifications.warning('Cart is empty', 'Add assets to the cart before completing the issue.');
      return;
    }

    const allSizingCandidates = buildIssueSizingSelectionCandidates({
      ieCartItems: ieTab.cartItems,
      additionalCartItems: additionalTab.cartItems,
      buyProgramCartItems,
      catalogChildren: allCatalogChildren,
    });

    // Filter out candidates where the same size is already saved in the person's sizing profile
    const existingSizingSet = new Set(
      existingPersonSizing.map((e) => {
        const data = e.entity_data as { parent_id?: string; catalog_id?: string };
        return `${data.parent_id}::${data.catalog_id}`;
      }),
    );
    const sizingCandidates = allSizingCandidates.filter(
      (c) => !existingSizingSet.has(`${c.parentCatalogId}::${c.catalogId}`),
    );

    if (sizingCandidates.length > 0) {
      setPendingSizingCandidates(sizingCandidates);
      setSelectedSizingCandidateIds(
        sizingCandidates.map((candidate) => `${candidate.parentCatalogId}::${candidate.catalogId}`),
      );
      setSizingReviewOpen(true);
      return;
    }

    await continueIssueCompletion([]);
  };

  // key={activeTab} forces React to unmount/remount the content subtree when
  // the tab changes, preventing stale catalog display in saved-cart sessions (#496).
  const plannerContent = (
    <Fragment key={activeTab}>
      {!isRestrictedProgramAssignOnly && activeTab === 0
        ? ieTab.content
        : !isRestrictedProgramAssignOnly && activeTab === 1
          ? additionalTab.content
          : !isRestrictedProgramAssignOnly && activeTab === 2
            ? buyProgramTab.content
            : restrictedProgramTab.content}
    </Fragment>
  );

  const plannerToolbar = (
    <Stack spacing={2}>
      {isRestrictedProgramAssignOnly && (
        <Alert severity="warning">
          {isHr ? 'HR access' : 'Comptroller/Finance access'} —{' '}
          {restrictedProgram.accessNoticeSuffix}
        </Alert>
      )}
      <Tabs
        value={activeTab}
        onChange={(_event, nextTab: number) => {
          setActiveTab(nextTab);
        }}
        aria-label="Issue workflow sections"
      >
        {isRestrictedProgramAssignOnly
          ? [<Tab key="restrictedProgram" label={restrictedProgram.programLabel} />]
          : [
              <Tab key="ie" label={ieKitLabel} />,
              <Tab key="additional" label="THR (Additional Items)" />,
              // Buy Program tab is not accessible to the comptroller role (permission matrix).
              // Only one buy program may have items in the cart per transaction, so the
              // Buy Program tab is disabled while the restricted (buy) program cart has items.
              <Tab
                key="buyProgram"
                label="Buy Program"
                disabled={(restrictedProgramTab.cartItems?.length ?? 0) > 0}
              />,
              // The restricted (buy) program tab is visible to all authenticated roles.
              // Disabled while the (CORE) Buy Program cart has items — mutually exclusive.
              <Tab
                key="restrictedProgram"
                label={restrictedProgram.programLabel}
                disabled={(buyProgramTab.cartItems?.length ?? 0) > 0}
              />,
            ]}
      </Tabs>
      {!isRestrictedProgramAssignOnly && activeTab === 0
        ? ieTab.toolbar
        : !isRestrictedProgramAssignOnly && activeTab === 1
          ? additionalTab.toolbar
          : !isRestrictedProgramAssignOnly && activeTab === 2
            ? buyProgramTab.toolbar
            : restrictedProgramTab.toolbar}
    </Stack>
  );

  const ieCartItems = useMemo(() => ieTab.cartItems ?? [], [ieTab.cartItems]);
  const additionalItems = additionalTab.cartItems ?? [];
  const buyProgramItems = buyProgramTab.cartItems ?? [];
  const restrictedProgramItems = restrictedProgramTab.cartItems ?? [];
  const ieAvailability = useMemo<Record<string, number>>(() => {
    const plannerItems = ieTab.plannerItems ?? [];
    const availabilityByPlannerId = plannerItems.reduce<Record<string, number>>((acc, item) => {
      acc[item.id] = Math.max(item.need, 0);
      return acc;
    }, {});

    ieCartItems.forEach((item) => {
      const plannerId = item.plannerItemId;
      if (!plannerId) {
        return;
      }

      const remaining = availabilityByPlannerId[plannerId] ?? 0;
      availabilityByPlannerId[plannerId] = Math.max(remaining - item.assetIds.length, 0);
    });

    return availabilityByPlannerId;
  }, [ieCartItems, ieTab.plannerItems]);
  const hasItems =
    ieCartItems.length > 0 ||
    additionalItems.length > 0 ||
    buyProgramItems.length > 0 ||
    restrictedProgramItems.length > 0 ||
    placeholders.length > 0;

  const [isForceClosing, setIsForceClosing] = useState(false);

  const forceClose = useCallback(() => {
    log.debug('forceClose triggered - initiating exit sequence');
    setShowExitPrompt(false);
    setIsForceClosing(true);
  }, []);

  const cancelIssue = useCallback(
    async (closeStrategy: 'direct' | 'dialog') => {
      if (isCanceling) return;
      setIsCanceling(true);

      try {
        if (customerId) {
          const clearResult = await cartService.clearCart(customerId);
          if (!clearResult.success) {
            notifications.error('Error', clearResult.message ?? 'Failed to cancel the issue cart.');
            return;
          }

          await cartService.clearSizeAllocationCarts(customerId);
        }

        const cartId = cartData?.cart?.id;
        if (cartId) {
          try {
            await cartService.deleteCart(cartId);
          } catch (error) {
            log.debug('Failed to delete cart during cancel', { error: String(error), cartId });
          }
        }

        if (customerId) {
          await queryClient.invalidateQueries({ queryKey: cartQueryKeys.activeAll() });
          await queryClient.invalidateQueries({ queryKey: cartQueryKeys.cart(customerId) });
        }

        buyProgramTab.actions.clear();

        if (closeStrategy === 'dialog') {
          forceClose();
        } else {
          onClose();
        }
      } catch (error) {
        log.error('Failed to cancel issue', { error: String(error) });
        notifications.error('Error', 'Failed to cancel the issue.');
      } finally {
        setIsCanceling(false);
      }
    },
    [
      buyProgramTab.actions,
      cartData?.cart?.id,
      customerId,
      forceClose,
      isCanceling,
      notifications,
      onClose,
      queryClient,
    ],
  );

  const handleReturnToFrontWindow = () => {
    if (hasItems) {
      setShowExitPrompt(true);
    } else {
      log.debug('Exiting IssueWorkflowModal - no items in cart');
      if (cartData?.cart?.id && customerId) {
        void cancelIssue('direct');
      } else {
        onClose();
      }
    }
  };

  const handleCancelIssue = async () => {
    log.info('handleCancelIssue triggered - canceling issue');
    await cancelIssue('dialog');
  };

  const sizeAllocationPlaceholders: SizeAllocationPlaceholder[] = useMemo(() => {
    return placeholders.map((placeholder) => {
      const meta = (catalogLookup[placeholder.parent_catalog_id] ?? {}) as {
        title?: string;
      };

      return {
        id: placeholder.id,
        parentCatalogId: placeholder.parent_catalog_id,
        title: meta.title ?? 'Catalog item',
        quantity: placeholder.quantity,
      };
    });
  }, [catalogLookup, placeholders]);

  const sidebarContent = (
    <IssueCartSidebar
      ieCartItems={ieCartItems}
      sizeAllocationPlaceholders={sizeAllocationPlaceholders}
      additionalItems={additionalItems}
      buyProgramItems={buyProgramItems}
      restrictedProgramItems={restrictedProgramItems}
      ieAvailability={ieAvailability}
      additionalAvailability={additionalTab.availabilityMap}
      hasAddableItems={ieTab.hasAddableItems}
      hasRestrictedProgramAddableItems={restrictedProgramTab.hasAddableItems}
      hasIncompleteRestrictedProgramItems={restrictedProgramTab.hasIncompleteItems}
      hasItems={hasItems}
      plannerActionId={ieTab.plannerActionId}
      isBulkProcessing={ieTab.isBulkProcessing}
      isCompleting={isCompleting}
      completionError={completionError}
      signatureAcknowledged={signatureAcknowledged}
      isComptroller={isRestrictedProgramAssignOnly}
      signaturePreviewSrc={signaturePreviewSrc}
      onClearSignature={capturedSignature ? handleClearSignature : undefined}
      onShowCustomerSummary={() => setCustomerSummaryOpen(true)}
      onAddAllAvailable={() => {
        void ieTab.actions.addAllAvailable();
      }}
      onAddRestrictedProgramAllAvailable={() => {
        void restrictedProgramTab.actions.addAllAvailable();
      }}
      onUpdateRestrictedProgramPricing={restrictedProgramTab.actions.updatePricing}
      onUpdateRestrictedProgramReceiptDate={restrictedProgramTab.actions.updateReceiptDate}
      onAdjustPlannerQuantity={(itemId, delta) => {
        void ieTab.actions.adjustQuantity(itemId, delta);
      }}
      onRemovePlannerItem={ieTab.actions.removeItem}
      onRemovePlaceholder={handleRemovePlaceholder}
      onAdjustAdditionalQuantity={additionalTab.actions.adjustQuantity}
      onRemoveAdditionalItem={additionalTab.actions.removeItem}
      onAdjustBuyProgramQuantity={buyProgramTab.actions.adjustQuantity}
      onRemoveBuyProgramItem={buyProgramTab.actions.removeItem}
      onAdjustRestrictedProgramQuantity={restrictedProgramTab.actions.adjustQuantity}
      onRemoveRestrictedProgramItem={restrictedProgramTab.actions.removeItem}
      onSyncToScanner={handleSyncToScanner}
      scanningStatus={cartData?.cart?.scanning_status}
      isWaitingForScan={isWaitingForScan}
      isCheckingForScanUpdates={isCheckingForScanUpdates}
      claimedByDisplayName={cartData?.cart?.claimed_by_display_name}
      onCaptureSignature={handleCaptureSignature}
      onSkipSignature={handleSkipSignature}
      onCompleteIssue={handleCompleteIssue}
      onCancelSync={handleCancelSync}
    />
  );

  const subtitle = useMemo(() => {
    return [unitName, memberName].filter(Boolean).join(' • ');
  }, [memberName, unitName]);

  const closeButton = (
    <Button
      variant="outlined"
      startIcon={<WestIcon />}
      onClick={handleReturnToFrontWindow}
      sx={{ whiteSpace: 'nowrap' }}
    >
      {isRestrictedProgramAssignOnly
        ? `Return to ${restrictedProgram.menuLabel}`
        : 'Return to Front-Window'}
    </Button>
  );

  return (
    <>
      <WorkflowModalLayout
        open={open}
        onClose={handleReturnToFrontWindow}
        title={iemLabels.issue_workflow_title}
        subTitle={subtitle}
        disableFocusTrap={true}
        plannerTitle="Issue Planner"
        plannerToolbar={plannerToolbar}
        plannerContent={plannerContent}
        sidebarTitle="Issue Cart"
        sidebarContent={sidebarContent}
        closeLabel="Close issue workflow"
        closeButton={closeButton}
      />

      <SignatureCaptureDialog
        open={signatureCaptureOpen}
        title={`Capture ${person.label} Signature`}
        initialSignature={capturedSignature}
        onCancel={() => setSignatureCaptureOpen(false)}
        onSave={handleSignatureSaved}
      />

      <IssueCustomerSummaryModal
        open={customerSummaryOpen}
        onClose={() => setCustomerSummaryOpen(false)}
        unitName={unitName}
        memberName={memberName}
        ieCartItems={ieCartItems}
        iePlannerItems={ieTab.plannerItems}
        sizeAllocationPlaceholders={sizeAllocationPlaceholders}
        additionalItems={additionalItems}
        buyProgramItems={buyProgramItems}
        restrictedProgramItems={restrictedProgramItems}
      />

      <Dialog
        open={sizingReviewOpen}
        onClose={() => setSizingReviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Save Sizes for Future Issues?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Choose which issued sizes should be saved to this {person.lower}&apos;s sizing profile.
          </DialogContentText>
          <List disablePadding>
            {pendingSizingCandidates.map((candidate) => {
              const candidateKey = `${candidate.parentCatalogId}::${candidate.catalogId}`;
              const checked = selectedSizingCandidateIds.includes(candidateKey);
              const subtitleParts = [
                candidate.quantity > 1 ? `Qty ${candidate.quantity}` : null,
              ].filter(Boolean);

              return (
                <ListItem key={candidateKey} disableGutters divider>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={(_event, nextChecked) => {
                          setSelectedSizingCandidateIds((current) => {
                            if (nextChecked) {
                              return current.includes(candidateKey)
                                ? current
                                : [...current, candidateKey];
                            }
                            return current.filter((value) => value !== candidateKey);
                          });
                        }}
                      />
                    }
                    label={
                      <Stack spacing={0.25}>
                        <Box component="span">
                          {candidate.sizeLabel
                            ? `${candidate.itemName} (${candidate.sizeLabel})`
                            : candidate.itemName}
                        </Box>
                        {subtitleParts.length > 0 ? (
                          <Box component="span" sx={{ color: 'text.secondary', fontSize: 13 }}>
                            {subtitleParts.join(' • ')}
                          </Box>
                        ) : null}
                      </Stack>
                    }
                    sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
                  />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSizingReviewOpen(false)}>Cancel</Button>
          <Button onClick={() => setSelectedSizingCandidateIds(sizingCandidateKeys)}>
            Select all
          </Button>
          <Button variant="contained" onClick={() => void handleSizingReviewConfirm()}>
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showExitPrompt}
        onClose={() => setShowExitPrompt(false)}
        TransitionProps={{
          onExited: () => {
            if (isForceClosing) {
              log.debug('Exit dialog transition complete - calling onClose');
              onClose();
              setIsForceClosing(false);
            }
          },
        }}
        aria-labelledby="exit-confirmation-title"
        aria-describedby="exit-confirmation-description"
      >
        <DialogTitle id="exit-confirmation-title">Items in Cart</DialogTitle>
        <DialogContent>
          <DialogContentText id="exit-confirmation-description">
            You have items in your issue cart. You can save your progress for later or cancel the
            issue to remove the reserved items.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelIssue}
            color="error"
            disabled={isCanceling || isWaitingForScan}
          >
            Cancel Issue
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setShowExitPrompt(false)} color="inherit" disabled={isCanceling}>
            Back to Cart
          </Button>
          <Button
            onClick={() => {
              forceClose();
            }}
            variant="contained"
            color="primary"
            autoFocus
            disabled={isCanceling}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <DocumentFieldsModal
        open={documentFieldsModalOpen}
        onClose={handleDocumentFieldsModalClose}
        onConfirm={(editedPayload) => {
          // Capture the SF 1034 signature BEFORE generation so it is embedded in
          // the document payload (#510). If a signature was already captured (e.g.
          // via the cart signature step) generate directly.
          if (capturedSignatureRef.current) {
            void executeIssueCompletion(editedPayload);
            return;
          }
          pendingBuyProgramPayloadRef.current = editedPayload ?? {};
          setSf1034SignOpen(true);
        }}
        onSkip={() => void executeIssueCompletion()}
        skipLabel="Skip Document"
        cancelLabel="Return to Cart"
        previewData={documentPreviewData}
        isLoading={isLoadingPreview}
        isGenerating={isGeneratingPdf}
        title="Complete Issue - SF 1034"
        subtitle="Review the SF 1034 form details before completing the Buy Program issue. Required fields are marked with an asterisk (*). You can skip to upload your own document later."
        confirmLabel="Create Document"
        generatingLabel="Completing Issue..."
      />

      {/* SF 1034 Digital Signature — captured BEFORE generation (#510) */}
      <Sf1034SignatureDialog
        open={sf1034SignOpen}
        onCapture={(sig) => {
          setSf1034SignOpen(false);
          capturedSignatureRef.current = sig;
          setCapturedSignature(sig);
          const payload = pendingBuyProgramPayloadRef.current;
          pendingBuyProgramPayloadRef.current = null;
          void executeIssueCompletion(payload ?? undefined);
        }}
        onSkip={() => {
          setSf1034SignOpen(false);
          const payload = pendingBuyProgramPayloadRef.current;
          pendingBuyProgramPayloadRef.current = null;
          void executeIssueCompletion(payload ?? undefined);
        }}
      />

      <RestrictedProgramCompleteIssueDialog
        open={restrictedProgramCompleteIssueDialogOpen}
        onClose={() => {
          setRestrictedProgramCompleteIssueDialogOpen(false);
          pendingIssueDataRef.current = null;
        }}
        onConfirm={(restrictedProgramPayload) => {
          setRestrictedProgramCompleteIssueDialogOpen(false);
          // Build the full SF1034 document payload from restricted (buy) program cart items.
          // The dialog only collects form fields (SSN, LOA, etc.) — the mapping
          // validator requires `items` (descriptions + amounts) and top-level
          // fields (`name`, `documentId`) that must be merged in here.
          const restrictedProgramPdfItems: PdfFormItem[] = restrictedProgramTab.cartItems.map(
            (item) => ({
              name: item.catalog.displayName ?? item.catalog.name,
              lin: item.catalog.lin || undefined,
              nsn: item.catalog.nsn || undefined,
              quantity: item.quantity || 1,
              unitPrice: item.actualPrice || 0,
              tax: item.tax || 0,
              shipping: item.shipping || 0,
              receiptDate: item.receiptDate || undefined,
              loa: (restrictedProgramPayload as Record<string, unknown>).itemLoaOverrides
                ? (
                    (restrictedProgramPayload as Record<string, unknown>)
                      .itemLoaOverrides as Record<string, string>
                  )[item.catalog.id]
                : item.catalog.loa || undefined,
            }),
          );
          const fullRestrictedProgramPayload: Record<string, unknown> = {
            documentId: '1034',
            isSocBuyProgram: true,
            name: memberName || '',
            payeeSquadronSection: customer?.unit || '',
            organization: { name: unitName, location: unitName },
            transactionInfo: { timestamp: new Date().toISOString() },
            remarks: [
              `${restrictedProgram.programLabel} Purchase - Issue Workflow`,
              user?.email ? `Issued by: ${user.email}` : null,
              `Items: ${restrictedProgramPdfItems.length}`,
            ]
              .filter(Boolean)
              .join('\n'),
            ...restrictedProgramPayload,
            items: restrictedProgramPdfItems,
          };
          // Ask whether to email the SF 1034 to the service member before generating.
          pendingRestrictedProgramPayloadRef.current = fullRestrictedProgramPayload;
          setRestrictedProgramSendToSmOpen(true);
        }}
        onSkip={() => {
          setRestrictedProgramCompleteIssueDialogOpen(false);
          void executeIssueCompletion();
        }}
        isSubmitting={isGeneratingPdf}
        defaultSquadronSection={customer?.unit ?? ''}
        defaultAccountableOfficer=""
        defaultCertifyingOfficer=""
        defaultSsn={customer?.ssn?.trim() ?? ''}
        cartItems={restrictedProgramTab.cartItems.map((item) => ({
          id: item.catalog.id,
          name: item.catalog.displayName || item.catalog.name,
          defaultLoa: item.catalog.loa ?? undefined,
        }))}
        signatureAcknowledged={signatureAcknowledged}
        signaturePreviewSrc={signaturePreviewSrc}
        onCaptureSignature={handleCaptureSignature}
        onClearSignature={capturedSignature ? handleClearSignature : undefined}
        onSkipSignature={handleSkipSignature}
      />

      {/* Restricted (buy) program — ask whether to email the SF 1034 to the service member.
          Either choice generates the document; only "Skip Document" (in the restricted
          program dialog above) skips generation. */}
      <Dialog
        open={restrictedProgramSendToSmOpen}
        onClose={() => {
          if (isGeneratingPdf) return;
          setRestrictedProgramSendToSmOpen(false);
          pendingRestrictedProgramPayloadRef.current = null;
          pendingIssueDataRef.current = null;
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Email SF 1034 to Service Member?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The SF 1034 will be generated for this {restrictedProgram.programLabel} issue. Would you
            like to email a copy to the service member?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            color="inherit"
            disabled={isGeneratingPdf}
            onClick={() => {
              setRestrictedProgramSendToSmOpen(false);
              const payload = pendingRestrictedProgramPayloadRef.current;
              pendingRestrictedProgramPayloadRef.current = null;
              void executeIssueCompletion({ ...(payload ?? {}), sendEmail: false });
            }}
          >
            No, Just Generate
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={isGeneratingPdf}
            onClick={() => {
              setRestrictedProgramSendToSmOpen(false);
              const payload = pendingRestrictedProgramPayloadRef.current;
              pendingRestrictedProgramPayloadRef.current = null;
              void executeIssueCompletion({
                ...(payload ?? {}),
                sendEmail: true,
                payeeEmail:
                  (payload?.payeeEmail as string | undefined) || memberEmailProp || undefined,
              });
            }}
          >
            Yes, Email Copy
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
